import React, { useEffect, useState } from "react";
import { FaBell, FaSearch, FaSyncAlt, FaWhatsapp } from "react-icons/fa";
import { toast } from "react-toastify";
import { API } from "../../../config/api";
import { get, post } from "../../../lib/http";
import { AD } from "../../adminUi.js";

const PAGE_SIZE = 20;

const cn = (...a) => a.filter(Boolean).join(" ");
const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleString(undefined, { hour12: true }) : "—";
const inr = (n) =>
  Number.isFinite(Number(n)) ? `₹${Number(n).toLocaleString("en-IN")}` : "—";

const WA_LABELS = {
  read: { label: "Read", hint: "Customer opened the message", checks: "✓✓" },
  delivered: { label: "Delivered", hint: "Reached customer's phone", checks: "✓✓" },
  sent: { label: "Sent", hint: "Sent from WhatsApp", checks: "✓" },
  failed: { label: "Failed", hint: "Delivery failed", checks: "!" },
  skipped: { label: "Skipped", hint: "Not sent", checks: "—" },
  pending: { label: "Pending", hint: "Sending…", checks: "…" },
  not_sent: { label: "Not sent", hint: "No WhatsApp sent yet", checks: "" },
};

function WhatsAppStatusCell({ row, onUpdate }) {
  const whatsapp = row.whatsapp || {};
  const v = String(whatsapp.status || "not_sent").toLowerCase();
  const meta = WA_LABELS[v] || WA_LABELS.not_sent;
  const map = {
    sent: "bg-emerald-100 text-emerald-800 ring-emerald-200",
    delivered: "bg-teal-100 text-teal-800 ring-teal-200",
    read: "bg-sky-100 text-sky-800 ring-sky-200",
    failed: "bg-rose-100 text-rose-800 ring-rose-200",
    skipped: "bg-amber-100 text-amber-800 ring-amber-200",
    pending: "bg-slate-100 text-slate-700 ring-slate-200",
    not_sent: "bg-gray-100 text-gray-600 ring-gray-200",
  };
  const cls = map[v] || map.not_sent;
  const timeAt =
    v === "read"
      ? whatsapp.readAt
      : v === "delivered"
        ? whatsapp.deliveredAt
        : whatsapp.sentAt;

  const [sending, setSending] = useState(false);
  const bookingId = row.bookingId;
  const canSend = Boolean(bookingId && row.mobile && row.mobile !== "—");
  const isSent = ["sent", "delivered", "read"].includes(v);

  const handleSend = async () => {
    if (!bookingId || sending) return;
    if (!canSend) {
      toast.error("Customer mobile not available");
      return;
    }
    try {
      setSending(true);
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
      const res = await post(
        API.dashboard.bookingNotificationsResendWhatsApp(bookingId),
        {},
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        }
      );
      toast.success(res?.message || "WhatsApp sent");
      onUpdate?.(bookingId, { whatsapp: res?.whatsapp });
    } catch (err) {
      toast.error(err?.message || "Failed to send WhatsApp");
      if (err?.whatsapp) onUpdate?.(bookingId, { whatsapp: err.whatsapp });
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <span
        title={whatsapp.error || meta.hint}
        className={cn(
          "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ring-1",
          cls,
          v === "read" && "ring-sky-300"
        )}
      >
        {meta.checks ? (
          <span className={v === "read" ? "text-sky-600" : "opacity-80"}>
            {meta.checks}
          </span>
        ) : null}
        {meta.label}
      </span>
      {timeAt ? (
        <div className="mt-1 text-[10px] text-slate-500">{fmtDateTime(timeAt)}</div>
      ) : null}
      {whatsapp.error && !isSent ? (
        <div
          className="mt-1 text-[10px] text-rose-600 max-w-[160px] truncate"
          title={whatsapp.error}
        >
          {whatsapp.error}
        </div>
      ) : null}
      {!isSent && canSend ? (
        <button
          type="button"
          onClick={handleSend}
          disabled={sending}
          className={cn(
            "mt-2 block rounded-lg px-2.5 py-1 text-[11px] font-semibold",
            "bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-60"
          )}
        >
          {sending ? "Sending…" : "Send WhatsApp"}
        </button>
      ) : null}
      {isSent && canSend ? (
        <button
          type="button"
          onClick={handleSend}
          disabled={sending}
          title="Resend WhatsApp pass"
          className={cn(
            "mt-2 block rounded-lg px-2.5 py-1 text-[11px] font-medium",
            "text-emerald-800 ring-1 ring-emerald-200 hover:bg-emerald-50 disabled:opacity-60"
          )}
        >
          {sending ? "Sending…" : "Resend"}
        </button>
      ) : null}
    </div>
  );
}

function SmsStatusCell({ row, onUpdate }) {
  const sms = row.sms || {};
  const status = String(sms.status || "not_sent").toLowerCase();
  const [sending, setSending] = useState(false);
  const bookingId = row.bookingId;
  const canSend = Boolean(bookingId && row.mobile && row.mobile !== "—");
  const isSent = status === "sent";

  const handleSend = async () => {
    if (!bookingId || sending) return;
    if (!canSend) {
      toast.error("Customer mobile not available");
      return;
    }
    try {
      setSending(true);
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
      const res = await post(
        API.dashboard.bookingNotificationsResendSms(bookingId),
        {},
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        }
      );
      toast.success(res?.message || "SMS sent");
      onUpdate?.(bookingId, { sms: res?.sms });
    } catch (err) {
      toast.error(err?.message || "Failed to send SMS");
      if (err?.sms) onUpdate?.(bookingId, { sms: err.sms });
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <ChannelBadge status={sms.status} />
      {sms.sentAt ? (
        <div className="mt-1 text-[10px] text-slate-400">
          {fmtDateTime(sms.sentAt)}
        </div>
      ) : null}
      {sms.message && !isSent ? (
        <div
          className={cn(
            "mt-1 text-[10px] max-w-[140px] truncate",
            status === "failed" ? "text-rose-600" : "text-slate-400"
          )}
          title={sms.message}
        >
          {sms.message}
        </div>
      ) : null}
      {!isSent && canSend ? (
        <button
          type="button"
          onClick={handleSend}
          disabled={sending}
          className={cn(
            "mt-2 block rounded-lg px-2.5 py-1 text-[11px] font-semibold",
            "bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-60"
          )}
        >
          {sending ? "Sending…" : "Send SMS"}
        </button>
      ) : null}
      {isSent && canSend ? (
        <button
          type="button"
          onClick={handleSend}
          disabled={sending}
          title="Resend ticket SMS"
          className={cn(
            "mt-2 block rounded-lg px-2.5 py-1 text-[11px] font-medium",
            "text-sky-800 ring-1 ring-sky-200 hover:bg-sky-50 disabled:opacity-60"
          )}
        >
          {sending ? "Sending…" : "Resend"}
        </button>
      ) : null}
    </div>
  );
}

function ChannelBadge({ status, title }) {
  const v = String(status || "not_sent").toLowerCase();
  const map = {
    sent: "bg-emerald-100 text-emerald-800 ring-emerald-200",
    delivered: "bg-teal-100 text-teal-800 ring-teal-200",
    read: "bg-sky-100 text-sky-800 ring-sky-200",
    failed: "bg-rose-100 text-rose-800 ring-rose-200",
    skipped: "bg-amber-100 text-amber-800 ring-amber-200",
    pending: "bg-slate-100 text-slate-700 ring-slate-200",
    not_sent: "bg-gray-100 text-gray-600 ring-gray-200",
  };
  const cls = map[v] || map.not_sent;
  return (
    <span
      title={title || status}
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1",
        cls
      )}
    >
      {status || "not_sent"}
    </span>
  );
}

function BoolBadge({ sent }) {
  return sent ? (
    <ChannelBadge status="sent" />
  ) : (
    <ChannelBadge status="not_sent" />
  );
}

function EmailStatusCell({ row, onSent }) {
  const [sending, setSending] = useState(false);
  const sent = Boolean(row.email?.sent);
  const bookingId = row.bookingId;
  const canSend = Boolean(bookingId && row.customerEmail);

  const handleSend = async () => {
    if (!bookingId || sending) return;
    if (!row.customerEmail) {
      toast.error("Customer email not available");
      return;
    }
    try {
      setSending(true);
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
      const res = await post(
        API.dashboard.bookingNotificationsResendEmail(bookingId),
        {},
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        }
      );
      toast.success(res?.message || "Email sent");
      onSent?.(bookingId, res?.email);
    } catch (err) {
      toast.error(err?.message || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <BoolBadge sent={sent} />
      {row.email?.sentAt ? (
        <div className="mt-1 text-[10px] text-slate-400">
          {fmtDateTime(row.email.sentAt)}
        </div>
      ) : null}
      {!sent && canSend ? (
        <button
          type="button"
          onClick={handleSend}
          disabled={sending}
          className={cn(
            "mt-2 block rounded-lg px-2.5 py-1 text-[11px] font-semibold",
            "bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-60"
          )}
        >
          {sending ? "Sending…" : "Send email"}
        </button>
      ) : null}
      {sent && canSend ? (
        <button
          type="button"
          onClick={handleSend}
          disabled={sending}
          title="Resend ticket email"
          className={cn(
            "mt-2 block rounded-lg px-2.5 py-1 text-[11px] font-medium",
            "text-amber-800 ring-1 ring-amber-200 hover:bg-amber-50 disabled:opacity-60"
          )}
        >
          {sending ? "Sending…" : "Resend"}
        </button>
      ) : null}
    </div>
  );
}

export default function AdminBookingNotifications() {
  const [rows, setRows] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedEvent, setSelectedEvent] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
        const res = await get(API.events.getEvent, {
          headers: { Authorization: token ? `Bearer ${token}` : undefined },
        });
        if (alive && res?.events) setEvents(res.events);
      } catch (err) {
        console.error("getEvents error:", err);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const fetchRows = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
      const params = new URLSearchParams({
        page: String(page),
        limit: String(PAGE_SIZE),
      });
      if (q.trim()) params.set("q", q.trim());
      if (selectedEvent) params.set("eventId", selectedEvent);

      const res = await get(
        `${API.dashboard.bookingNotifications}?${params}`,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        }
      );

      setRows(Array.isArray(res?.rows) ? res.rows : []);
      setTotalPages(res?.totalPages || 1);
      setTotal(res?.total ?? 0);
    } catch (err) {
      console.error("booking-notifications:", err);
      setError(err?.message || "Unable to load notification logs.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, q, selectedEvent]);

  // Auto-refresh so read/delivered updates from webhook appear without manual refresh
  useEffect(() => {
    const id = setInterval(() => {
      fetchRows(true);
    }, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, q, selectedEvent]);

  const onSearch = (e) => {
    e.preventDefault();
    setQ(searchInput);
    setPage(1);
  };

  const handleEmailSent = (bookingId, emailPatch) => {
    setRows((prev) =>
      prev.map((r) =>
        String(r.bookingId) === String(bookingId)
          ? {
              ...r,
              email: {
                sent: true,
                sentAt: emailPatch?.sentAt || new Date().toISOString(),
                messageId: emailPatch?.messageId || r.email?.messageId,
              },
            }
          : r
      )
    );
  };

  const handleChannelUpdate = (bookingId, patch = {}) => {
    setRows((prev) =>
      prev.map((r) =>
        String(r.bookingId) === String(bookingId)
          ? {
              ...r,
              ...(patch.whatsapp ? { whatsapp: { ...r.whatsapp, ...patch.whatsapp } } : null),
              ...(patch.sms ? { sms: { ...r.sms, ...patch.sms } } : null),
            }
          : r
      )
    );
  };

  return (
    <div className={cn(AD.formWrapWide, AD.page)}>
      <div className={AD.headerRow}>
        <div>
          <nav className="mb-1 flex items-center gap-1.5 text-xs text-slate-500">
            <span>Admin</span>
            <span>/</span>
            <span className="font-medium text-slate-700">Booking notifications</span>
          </nav>
          <h1 className={AD.title}>Booking notifications</h1>
          <p className={AD.subtitle}>
            Paid bookings — WhatsApp: Sent → Delivered → Read (auto-refresh every 30s).
          </p>
        </div>
        <button type="button" onClick={() => fetchRows()} className={cn(AD.btnSecondary, "gap-2")}>
          <FaSyncAlt className="text-xs" />
          Refresh
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className={cn(AD.statCard, "flex items-center gap-3")}>
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 text-white">
            <FaBell className="text-sm" />
          </span>
          <div>
            <p className="text-xs text-slate-500">Paid bookings</p>
            <p className="text-xl font-bold text-slate-900">{total}</p>
          </div>
        </div>
        <div className={cn(AD.statCard, "flex items-center gap-3")}>
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 text-white">
            <FaWhatsapp className="text-sm" />
          </span>
          <div>
            <p className="text-xs text-slate-500">On this page</p>
            <p className="text-xl font-bold text-slate-900">{rows.length}</p>
          </div>
        </div>
      </div>

      <div className={AD.filterCard}>
        <form onSubmit={onSearch} className="grid gap-4 md:grid-cols-12">
          <div className="md:col-span-5">
            <label className={AD.fieldLabel}>Booking ID / code</label>
            <div className="relative mt-1">
              <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400" />
              <input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search booking code…"
                className={cn(AD.inputField, "pl-10")}
              />
            </div>
          </div>
          <div className="md:col-span-4">
            <label className={AD.fieldLabel}>Event</label>
            <select
              value={selectedEvent}
              onChange={(e) => {
                setSelectedEvent(e.target.value);
                setPage(1);
              }}
              className={cn(AD.select, "mt-1")}
            >
              <option value="">All events</option>
              {events.map((ev) => (
                <option key={ev._id} value={ev._id}>
                  {ev.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end md:col-span-3">
            <button type="submit" className={cn(AD.btnPrimary, "w-full")}>
              Search
            </button>
          </div>
        </form>
      </div>

      <div className={AD.tableShell}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px] text-sm">
            <thead className={AD.thead}>
              <tr>
                <th className="p-3">Booking ID</th>
                <th className="p-3">Customer</th>
                <th className="p-3">Event</th>
                <th className="p-3">Payment</th>
                <th className="p-3">Email</th>
                <th className="p-3">SMS</th>
                <th className="p-3">WhatsApp</th>
                <th className="p-3">Paid at</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-slate-500">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-rose-600">
                    {error}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-slate-500">
                    No records found.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr
                    key={r.paymentId}
                    className="border-t border-slate-100 hover:bg-amber-50/30 dark:border-gray-800 dark:hover:bg-gray-800/40"
                  >
                    <td className="p-3 font-mono text-xs font-semibold text-amber-900 dark:text-amber-200">
                      {r.bookingCode}
                    </td>
                    <td className="p-3">
                      <div className="font-medium">{r.customerName}</div>
                      <div className="text-xs text-slate-500">{r.mobile}</div>
                      {r.customerEmail ? (
                        <div className="text-xs text-slate-400 truncate max-w-[180px]">
                          {r.customerEmail}
                        </div>
                      ) : null}
                    </td>
                    <td className="p-3">
                      <div className="font-medium">{r.eventName}</div>
                      {r.eventDate ? (
                        <div className="text-xs text-slate-500">
                          {new Date(r.eventDate).toLocaleDateString()}
                        </div>
                      ) : null}
                    </td>
                    <td className="p-3">
                      <div>{inr(r.amount)}</div>
                      <div className="text-xs text-slate-500">
                        {r.paymentMethod} · {r.paymentStatus}
                      </div>
                    </td>
                    <td className="p-3">
                      <EmailStatusCell row={r} onSent={handleEmailSent} />
                    </td>
                    <td className="p-3">
                      <SmsStatusCell row={r} onUpdate={handleChannelUpdate} />
                    </td>
                    <td className="p-3">
                      <WhatsAppStatusCell row={r} onUpdate={handleChannelUpdate} />
                    </td>
                    <td className="p-3 text-xs text-slate-600 dark:text-slate-400 whitespace-nowrap">
                      {fmtDateTime(r.paidAt)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-600">
          Page {page} of {totalPages}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className={AD.btnSecondary}
          >
            Previous
          </button>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className={AD.btnSecondary}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
